import { IconComponentProps } from '@nexelem/icons';
import { ProLightClock } from '@nexelem/icons/ProLightClock';
import { format } from 'date-fns';
import { get } from 'lodash';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useFiltersContext } from '../hooks';
import { FilterValue, RegisterFunction } from '../types/filter.types';
import { Calendar, Chip, DropdownMenu, DateRange } from '@nexelem/components';

const DATE_LABEL_FORMAT = 'yyyy-MM-dd';

export type FilterChipDateRangeValue = DateRange | undefined;

type FilterChipDateRangeProps = {
  path: string;
  label: string;
  register: RegisterFunction;
  Icon?: FC<IconComponentProps>;
};

export const FilterChipDateRange = ({ path, label, register, Icon = ProLightClock }: FilterChipDateRangeProps) => {
  const { filters, onChange } = useFiltersContext();

  const filterValue = get(filters, path, {
    value: undefined,
    key: '',
  }) as FilterValue<FilterChipDateRangeValue>;

  useEffect(() => {
    register({
      path,
      isMultiple: false,
      value: undefined,
    });
  }, [path, register]);

  const handleDateChange = useCallback(
    (dateRange: FilterChipDateRangeValue) => {
      onChange(path, {
        value: dateRange,
        isMultiple: false,
      });
    },
    [onChange, path],
  );

  const date = filterValue.value;

  const labelOrDate = useMemo(
    () =>
      isNotEmpty(date) ? `${format(date.from, DATE_LABEL_FORMAT)} - ${format(date.to, DATE_LABEL_FORMAT)}` : label,
    [date, label],
  );

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <Chip label={labelOrDate} selected={isNotEmpty(date)} StartIcon={<Icon />} />
      </DropdownMenu.Trigger>
      <DropdownMenu.CalendarContainer>
        <Calendar
          resetOnSelect
          captionLayout="dropdown"
          mode="range"
          selected={filterValue.value}
          onSelect={handleDateChange}
        />
      </DropdownMenu.CalendarContainer>
    </DropdownMenu.Root>
  );
};

const isNotEmpty = (
  date?: FilterChipDateRangeValue,
): date is Required<Exclude<Partial<FilterChipDateRangeValue>, undefined>> => !!(date && date.from && date.to);
